import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { RESOURCES } from "./resources";
import { Loading, Empty } from "../../components/Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const toForm = (fields, item) => Object.fromEntries(fields.map((f) => {
  const v = item?.[f.k];
  if (f.type === "list") return [f.k, (v || []).join("\n")];
  if (f.type === "faqs") return [f.k, (v || []).map((x) => `${x.q} | ${x.a}`).join("\n")];
  if (f.type === "bool") return [f.k, v ?? (f.k === "published" || f.k.startsWith("shuffle"))];
  if (f.type === "multi") return [f.k, v || []];
  return [f.k, v ?? (f.type === "number" ? "" : f.type === "correct" ? 0 : "")];
}));

const fromForm = (fields, form) => Object.fromEntries(fields.map((f) => {
  const v = form[f.k];
  if (f.type === "list") return [f.k, String(v).split("\n").map((s) => s.trim()).filter(Boolean)];
  if (f.type === "faqs") return [f.k, String(v).split("\n").filter((l) => l.includes("|")).map((l) => { const [q, ...a] = l.split("|"); return { q: q.trim(), a: a.join("|").trim() }; })];
  if (f.type === "number" || f.type === "correct") return [f.k, v === "" ? 0 : Number(v)];
  if (f.type === "date" && v) return [f.k, String(v).slice(0, 10)];
  return [f.k, v];
}));

function Field({ f, form, setForm, refs }) {
  const v = form[f.k];
  const set = (val) => setForm({ ...form, [f.k]: val });
  const id = `rf-${f.k}`;
  const common = { id, className: "field", "data-testid": `rm-field-${f.k}` };
  let input;
  if (f.type === "textarea" || f.type === "list" || f.type === "faqs") input = <textarea {...common} rows={f.rows || 4} value={v} onChange={(e) => set(e.target.value)} />;
  else if (f.type === "bool") return <label className="flex gap-2 items-center text-sm font-semibold"><input type="checkbox" className="accent-[#dc2626] w-4 h-4" checked={!!v} onChange={(e) => set(e.target.checked)} data-testid={`rm-field-${f.k}`} />{f.label}</label>;
  else if (f.type === "select") input = <select {...common} value={v} onChange={(e) => set(e.target.value)}><option value="">Seçiniz</option>{Object.entries(f.options).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>;
  else if (f.type === "ref") input = <select {...common} value={v} onChange={(e) => set(e.target.value)}><option value="">Seçiniz</option>{(refs[f.source] || []).map((r) => <option key={r.id} value={r.id}>{r.title || `${r.name} (${r.email})`}</option>)}</select>;
  else if (f.type === "correct") {
    const opts = String(form.options || "").split("\n").map((s) => s.trim()).filter(Boolean);
    input = <select {...common} value={v} onChange={(e) => set(Number(e.target.value))}>{opts.map((o, i) => <option key={i} value={i}>{i + 1}) {o}</option>)}</select>;
  } else if (f.type === "multi") input = <div className="flex flex-wrap gap-2" data-testid={`rm-field-${f.k}`}>{Object.entries(f.options).map(([k, l]) => <label key={k} className={`px-2 py-1 border text-xs cursor-pointer ${v.includes(k) ? "bg-zinc-950 text-white" : ""}`}><input type="checkbox" className="hidden" checked={v.includes(k)} onChange={() => set(v.includes(k) ? v.filter((x) => x !== k) : [...v, k])} />{l}</label>)}</div>;
  else input = <input {...common} type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "time" ? "time" : "text"} value={f.type === "date" ? String(v).slice(0, 10) : v} required={f.required} onChange={(e) => set(e.target.value)} />;
  return <div><label className="label" htmlFor={id}>{f.label}{f.required && " *"}</label>{input}</div>;
}

export default function ResourceManager({ name: propName }) {
  const params = useParams();
  const name = propName || params.name;
  const cfg = RESOURCES[name];
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [refs, setRefs] = useState({});
  const load = useCallback(() => api.get(`/admin/r/${name}`, { params: { q } }).then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); }), [name, q]);
  useEffect(() => { setItems(null); load(); }, [load]);
  useEffect(() => {
    const need = new Set(cfg.fields.filter((f) => f.type === "ref").map((f) => f.source));
    need.forEach((s) => api.get(s === "students" ? "/admin/students" : `/admin/r/${s}`).then((r) => setRefs((p) => ({ ...p, [s]: r.data }))).catch(() => {}));
  }, [cfg]);
  const open = (item) => { setEdit(item || {}); setForm(toForm(cfg.fields, item)); };
  const save = async (e) => {
    e.preventDefault();
    const body = fromForm(cfg.fields, form);
    try {
      if (edit.id) await api.put(`/admin/r/${name}/${edit.id}`, body); else await api.post(`/admin/r/${name}`, body);
      toast.success("Kaydedildi"); setEdit(null); load();
    } catch (x) { toast.error(errMsg(x)); }
  };
  const del = async (it) => { if (!window.confirm("Bu kayıt silinsin mi?")) return; try { await api.delete(`/admin/r/${name}/${it.id}`); toast.success("Silindi"); load(); } catch (x) { toast.error(errMsg(x)); } };
  const cell = (it, [k, , fmt, refKey]) => {
    if (refKey) { const list = refKey === "user" ? refs.students : refs[refKey]; const r = (list || []).find((x) => x.id === it[k]); return r ? r.title || r.name : it[k]?.slice(0, 8); }
    const v = fmt ? fmt(it[k]) : it[k];
    return typeof v === "object" ? JSON.stringify(v) : String(v ?? "");
  };
  if (!cfg) return <Empty text="Modül bulunamadı" />;
  return (
    <div data-testid={`rm-${name}`}>
      <div className="flex flex-wrap justify-between gap-3 mb-5">
        <h1 className="text-2xl font-black">{cfg.title}</h1>
        <div className="flex gap-2">
          <div className="relative"><Search size={14} className="absolute left-3 top-3.5 text-zinc-400" /><input className="field !pl-8 !py-2.5" placeholder="Ara" value={q} onChange={(e) => setQ(e.target.value)} data-testid="rm-search" /></div>
          {!cfg.noCreate && <button className="btn-red !py-2.5" onClick={() => open(null)} data-testid="rm-add-btn"><Plus size={16} />Yeni</button>}
        </div>
      </div>
      {cfg.note && <p className="mb-4 text-sm border-l-4 border-zinc-900 bg-zinc-50 p-3">{cfg.note}</p>}
      {!items ? <Loading /> : items.length ? (
        <div className="overflow-x-auto bg-white border border-zinc-200">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-xs uppercase text-zinc-500"><tr>{cfg.columns.map(([k, l]) => <th key={k} className="px-4 py-3">{l}</th>)}<th className="px-4 py-3 w-24" /></tr></thead>
            <tbody>{items.map((it) => (
              <tr key={it.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`rm-row-${it.id}`}>
                {cfg.columns.map((c) => <td key={c[0]} className="px-4 py-3 max-w-[340px] truncate">{cell(it, c)}</td>)}
                <td className="px-4 py-3"><div className="flex gap-1">
                  <button className="p-2 hover:bg-zinc-200" onClick={() => open(it)} aria-label="Düzenle" data-testid={`rm-edit-${it.id}`}><Pencil size={15} /></button>
                  {!cfg.noCreate && <button className="p-2 hover:bg-red-100 text-[#dc2626]" onClick={() => del(it)} aria-label="Sil" data-testid={`rm-delete-${it.id}`}><Trash2 size={15} /></button>}
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <Empty text="Kayıt yok." />}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="rm-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Düzenle" : "Yeni kayıt"} – {cfg.title}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid gap-4">
            {cfg.fields.map((f) => <Field key={f.k} f={f} form={form} setForm={setForm} refs={refs} />)}
            <button className="btn-red" data-testid="rm-save-btn">Kaydet</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
